// src/pages/Videos.jsx
import React, { useMemo, useRef, useState, useEffect } from "react";
import TikTokEmbed from "../components/TikTokEmbed";
import { VIDEOS } from "../data/videos";
import { Helmet } from "react-helmet";

function TikTokIcon({ className = "" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      aria-hidden="true"
      fill="currentColor"
    >
      <path d="M16.5 3c.6 2.7 2.4 4.9 4.9 5.6v3.2c-2.1 0-4.1-.7-5.8-2v6.4c0 3.2-2.6 5.8-5.8 5.8S4 19.4 4 16.2s2.6-5.8 5.8-5.8c.5 0 1 .1 1.5.2v3.3c-.5-.2-1-.4-1.5-.4-1.4 0-2.6 1.2-2.6 2.6S8.4 18.8 9.8 18.8s2.6-1.2 2.6-2.6V3h4.1z" />
    </svg>
  );
}

export default function Videos() {
  // ✅ SEO (canonical domain + per-page URL)
  const SITE_URL = "https://www.guatemalangolf.com";
  const canonicalUrl = `${SITE_URL}/videos`;
  // Use your global OG image (stable). Later you can make a /og/videos.jpg if you want.
  const ogImage = `${SITE_URL}/og-image.jpg`;

  const STEP = 6; // how many to add each click
  const [visibleCount, setVisibleCount] = useState(STEP);

  // Track which range was just revealed (for fade-in)
  const [justRevealedFrom, setJustRevealedFrom] = useState(null);

  // Refs for smooth scroll
  const cardRefs = useRef({}); // { [id]: HTMLElement }

  const visibleVideos = useMemo(
    () => VIDEOS.slice(0, visibleCount),
    [visibleCount]
  );

  const canLoadMore = visibleCount < VIDEOS.length;

  // Smooth scroll to the first newly revealed card when visibleCount increases
  useEffect(() => {
    if (justRevealedFrom == null) return;

    const firstNewId = visibleVideos[justRevealedFrom];
    const el = firstNewId ? cardRefs.current[firstNewId] : null;

    if (el) {
      // slight delay to ensure DOM is painted
      requestAnimationFrame(() => {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }

    // Remove "new" styling after the animation window
    const t = setTimeout(() => setJustRevealedFrom(null), 900);
    return () => clearTimeout(t);
  }, [justRevealedFrom, visibleVideos]);

  function handleLoadMore() {
    setVisibleCount((current) => {
      const next = Math.min(current + STEP, VIDEOS.length);
      // mark the index where new items start
      setJustRevealedFrom(current);
      return next;
    });
  }

  return (
    <div className="space-y-8">
      <Helmet>
        <title>Golf Videos in Guatemala | Guatemalan Golf</title>
        <meta
          name="description"
          content="Watch golf videos from Guatemala — volcano backdrops, course highlights, travel clips, and the vibes. Curated from @guatemalangolf."
        />

        {/* ✅ Canonical + URL consistency */}
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:url" content={canonicalUrl} />

        {/* Optional: page-specific OG text (Google may use, social may ignore on SPA) */}
        <meta property="og:title" content="Golf Videos in Guatemala | Guatemalan Golf" />
        <meta
          property="og:description"
          content="Watch golf videos from Guatemala — volcano backdrops, course highlights, travel clips, and the vibes."
        />
        <meta property="og:image" content={ogImage} />
      </Helmet>

      {/* Header (premium “glass” card) */}
      <header className="rounded-3xl bg-white/80 border border-black/10 p-6 sm:p-8 backdrop-blur shadow-[0_22px_70px_rgba(0,0,0,0.08)]">
        <div className="flex items-start gap-3">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-gg-sand border border-black/10">
            <TikTokIcon className="h-5 w-5 text-gg-lava" />
          </span>

          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-gg-ink">
              Videos
            </h1>
            <p className="mt-2 text-gg-muted max-w-2xl">
              Reels from Guatemala — courses, swings, sunsets, and the vibes.
            </p>

            <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-gg-sand/70 border border-black/10 px-3 py-1 text-xs text-gg-muted">
              <span className="h-1.5 w-1.5 rounded-full bg-gg-lava" />
              Curated from{" "}
              <span className="font-semibold text-gg-ink">@guatemalangolf</span>
            </div>
          </div>
        </div>
      </header>

      {/* TikTok grid */}
      <div className="grid gap-6 md:grid-cols-2">
        {visibleVideos.map((id, idx) => {
          const isNew = justRevealedFrom != null && idx >= justRevealedFrom;

          return (
            <div
              key={id}
              ref={(node) => {
                if (node) cardRefs.current[id] = node;
              }}
              className={[
                "rounded-3xl bg-gg-sand p-4 sm:p-6 border border-black/10",
                "shadow-[0_20px_60px_rgba(0,0,0,0.10)]",
                // Hover lift
                "transition-transform duration-300 ease-out",
                "hover:-translate-y-1 hover:shadow-[0_26px_80px_rgba(0,0,0,0.14)]",
                // Fade-in for newly revealed items
                "transition-all duration-700 ease-out",
                isNew
                  ? "opacity-0 translate-y-2 animate-[ggFadeUp_700ms_ease-out_forwards]"
                  : "opacity-100 translate-y-0",
              ].join(" ")}
            >
              <TikTokEmbed videoId={id} username="guatemalangolf" />
            </div>
          );
        })}
      </div>

      {/* Load more */}
      <div className="flex items-center justify-center">
        {canLoadMore ? (
          <button
            type="button"
            onClick={handleLoadMore}
            className="inline-flex items-center justify-center rounded-full bg-white/80 border border-black/10 px-6 py-3 text-sm font-semibold text-gg-ink
                       backdrop-blur shadow-[0_18px_45px_rgba(0,0,0,0.10)]
                       hover:shadow-[0_22px_55px_rgba(0,0,0,0.14)]
                       hover:scale-[1.02] transition
                       focus:outline-none focus:ring-2 focus:ring-gg-lava/45"
          >
            Load more
          </button>
        ) : (
          <div className="text-xs text-gg-muted rounded-full bg-gg-sand/70 border border-black/10 px-4 py-2">
            You’re all caught up. ✅
          </div>
        )}
      </div>

      {/* CTA */}
      <div className="rounded-3xl bg-white/80 border border-black/10 p-6 sm:p-8 backdrop-blur shadow-[0_22px_70px_rgba(0,0,0,0.08)]">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gg-ink">
              Follow @guatemalangolf on TikTok
            </h2>
            <p className="mt-1 text-gg-muted">
              New clips drop regularly — tournaments, course views, and merch
              moments.
            </p>
          </div>

          <a
            href="https://www.tiktok.com/@guatemalangolf"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center rounded-full bg-gg-lava px-6 py-3 text-sm font-semibold text-white
                       shadow-[0_18px_45px_rgba(230,57,46,0.28)]
                       hover:bg-gg-ember hover:shadow-[0_22px_55px_rgba(255,122,26,0.22)]
                       hover:scale-[1.02] transition
                       focus:outline-none focus:ring-2 focus:ring-gg-lava/45"
          >
            Open TikTok Profile
          </a>
        </div>
      </div>

      {/* Local keyframes (scoped) */}
      <style>{`
        @keyframes ggFadeUp {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}